import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MainLayout from "../../../components/layout/MainLayout";
import { useMasterData } from "../../../hooks/useMasterData";
import { feesApi } from "../api/feesApi";

const amount = (value: any) => Number(value || 0);

const DaybookReceiptPage: React.FC = () => {
  const { masters, fetchAcademicSessions } = useMasterData();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [sessionId, setSessionId] = useState("");
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAcademicSessions();
  }, [fetchAcademicSessions]);

  const filters = useMemo(
    () => ({ date, sessionId: sessionId || undefined }),
    [date, sessionId],
  );

  const loadReport = async () => {
    if (!date) {
      setError("Date is required");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await feesApi.daybook(filters);
      setReport(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load daybook");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadExport = async (format: "csv" | "xlsx") => {
    if (!date) {
      setError("Date is required for export");
      return;
    }
    try {
      const file = await feesApi.getDaybookExportBlob({ date, sessionId: sessionId || undefined, format });
      const url = URL.createObjectURL(file.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to export daybook");
    }
  };

  const printReport = () => {
    if (!report) return;
    const rows = (report.transactions || [])
      .map(
        (tx: any) => `
          <tr>
            <td>${new Date(tx.time).toLocaleTimeString()}</td>
            <td>${tx.student || "-"}</td>
            <td>${tx.receiptNo || "-"}</td>
            <td>${tx.type}</td>
            <td>${tx.mode}</td>
            <td style="text-align:right">${amount(tx.amount).toFixed(2)}</td>
          </tr>
        `,
      )
      .join("");

    const popup = window.open("", "_blank", "width=1000,height=700");
    if (!popup) return;

    popup.document.write(`
      <html>
        <head>
          <title>Daybook Receipt - ${report.date}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 16px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
            th { background: #f5f5f5; text-align: left; }
            .cards { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin-top: 10px; }
            .card { border: 1px solid #ddd; padding: 8px; border-radius: 4px; }
          </style>
        </head>
        <body>
          <h2>Daybook Receipt</h2>
          <div>Date: ${report.date}</div>
          <div class="cards">
            <div class="card">Opening: ${amount(report.opening).toFixed(2)}</div>
            <div class="card">Collection: ${amount(report.collection).toFixed(2)}</div>
            <div class="card">Refund: ${amount(report.refund).toFixed(2)}</div>
            <div class="card">Net: ${amount(report.net).toFixed(2)}</div>
            <div class="card">Closing: ${amount(report.closing).toFixed(2)}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Student</th>
                <th>Receipt No</th>
                <th>Type</th>
                <th>Mode</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>${rows || '<tr><td colspan="6">No transactions</td></tr>'}</tbody>
          </table>
        </body>
      </html>
    `);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
          Daybook Receipt
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                required
                type="date"
                label="Date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Academic Session (Optional)"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
              >
                <MenuItem value="">All Sessions</MenuItem>
                {masters.academicSessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" onClick={loadReport} disabled={loading}>
                {loading ? "Loading..." : "Get Daybook"}
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={1}>
                <Grid item xs={4}><Button fullWidth variant="outlined" onClick={() => downloadExport("xlsx")}>Excel</Button></Grid>
                <Grid item xs={4}><Button fullWidth variant="outlined" onClick={() => downloadExport("csv")}>CSV</Button></Grid>
                <Grid item xs={4}><Button fullWidth variant="outlined" onClick={printReport}>Print</Button></Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4} lg={2}><Paper sx={{ p: 2 }}><Typography variant="caption">Opening Balance</Typography><Typography variant="h6">{amount(report?.opening).toFixed(2)}</Typography></Paper></Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}><Paper sx={{ p: 2 }}><Typography variant="caption">Total Collection</Typography><Typography variant="h6">{amount(report?.collection).toFixed(2)}</Typography></Paper></Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}><Paper sx={{ p: 2 }}><Typography variant="caption">Total Refund</Typography><Typography variant="h6">{amount(report?.refund).toFixed(2)}</Typography></Paper></Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}><Paper sx={{ p: 2 }}><Typography variant="caption">Net Collection</Typography><Typography variant="h6">{amount(report?.net).toFixed(2)}</Typography></Paper></Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}><Paper sx={{ p: 2 }}><Typography variant="caption">Closing Balance</Typography><Typography variant="h6">{amount(report?.closing).toFixed(2)}</Typography></Paper></Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Mode-wise Breakdown</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={3}><Typography variant="body2">CASH: {amount(report?.modeWise?.CASH).toFixed(2)}</Typography></Grid>
            <Grid item xs={6} sm={4} md={3}><Typography variant="body2">UPI: {amount(report?.modeWise?.UPI).toFixed(2)}</Typography></Grid>
            <Grid item xs={6} sm={4} md={2}><Typography variant="body2">ONLINE: {amount(report?.modeWise?.ONLINE).toFixed(2)}</Typography></Grid>
            <Grid item xs={6} sm={6} md={2}><Typography variant="body2">CHEQUE: {amount(report?.modeWise?.CHEQUE).toFixed(2)}</Typography></Grid>
            <Grid item xs={12} sm={6} md={2}><Typography variant="body2">BANK TRANSFER: {amount(report?.modeWise?.BANK_TRANSFER).toFixed(2)}</Typography></Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Transaction Table</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Receipt No</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(report?.transactions || []).length ? (report.transactions || []).map((tx: any, index: number) => (
                <TableRow key={`${tx.time}-${index}`}>
                  <TableCell>{new Date(tx.time).toLocaleTimeString()}</TableCell>
                  <TableCell>{tx.student || "-"}</TableCell>
                  <TableCell>{tx.receiptNo || "-"}</TableCell>
                  <TableCell>{tx.type}</TableCell>
                  <TableCell>{tx.mode}</TableCell>
                  <TableCell align="right">{amount(tx.amount).toFixed(2)}</TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">No transactions found for selected date</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </MainLayout>
  );
};

export default DaybookReceiptPage;
